import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  FileText,
  MessageSquare,
  Settings,
  BarChart3,
  Shield,
  Upload,
  Eye,
  TestTube,
  Database,
  Activity,
  Crown,
  Home
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';

const adminMenuItems = [
  {
    title: 'Dashboard',
    url: '/secure-admin-feel-2024',
    icon: Home,
    description: 'Vue d\'ensemble'
  },
  {
    title: 'Documents',
    url: '/secure-admin-feel-2024/documents',
    icon: FileText,
    description: 'Gestion RAG'
  },
  {
    title: 'Conversations',
    url: '/secure-admin-feel-2024/conversations',
    icon: MessageSquare,
    description: 'Monitoring temps réel'
  },
  {
    title: 'Agent Strict',
    url: '/secure-admin-feel-2024/strict-agent',
    icon: TestTube,
    description: 'Tests & validation'
  },
  {
    title: 'Configuration',
    url: '/secure-admin-feel-2024/prompt',
    icon: Settings,
    description: 'Prompts & paramètres'
  }
];

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path: string) => {
    // Exact match pour le dashboard
    if (path === '/secure-admin-feel-2024' && currentPath === '/secure-admin-feel-2024') {
      return true;
    }
    // Pour les autres routes, vérifier si le path actuel commence par l'URL du menu
    return path !== '/secure-admin-feel-2024' && currentPath.startsWith(path);
  };

  const isExpanded = adminMenuItems.some((item) => isActive(item.url));

  const getNavClasses = (itemPath: string) => {
    const active = isActive(itemPath);
    return active 
      ? 'bg-primary text-primary-foreground font-medium' 
      : 'hover:bg-muted/50 text-muted-foreground hover:text-foreground';
  };

  return (
    <Sidebar
      className={collapsed ? 'w-14' : 'w-64'}
      collapsible="icon"
    >
      <SidebarContent>
        {/* En-tête Admin */}
        <div className={`p-4 border-b ${collapsed ? 'px-2' : ''}`}>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Crown className="h-4 w-4 text-primary-foreground" />
            </div>
            {!collapsed && (
              <div>
                <h2 className="text-lg font-semibold">Admin FEEL</h2>
                <p className="text-xs text-muted-foreground">Console d'administration</p>
              </div>
            )}
          </div>
        </div>

        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'hidden' : ''}>
            Navigation
          </SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              {adminMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === '/secure-admin-feel-2024'}
                      className={getNavClasses(item.url)}
                    >
                      <item.icon className="h-4 w-4" />
                      {!collapsed && (
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{item.title}</span>
                          <span className="text-xs opacity-70">{item.description}</span>
                        </div>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Stats en bas si non collapsed */}
        {!collapsed && (
          <div className="mt-auto p-4 border-t">
            <div className="text-xs text-muted-foreground space-y-1">
              <div className="flex items-center gap-2">
                <Activity className="h-3 w-3" />
                <span>Version 3.0</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-3 w-3" />
                <span>Sécurisé</span>
              </div>
            </div>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  );
}